import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PortalShell from "@/components/portal/PortalShell";
import { AccessLinkForm } from "@/components/portal/AccessLinkForm";
import { useAuth, roleHome } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Loader2, Shield, Zap, ClipboardList, Building2, Handshake, Wallet, FileText, TrendingUp, Lock, Eye, EyeOff, MessagesSquare, FileSignature, Headphones, Users, Megaphone } from "lucide-react";
import { ExplainerProvider, WatchExplainerButton } from "@/components/network/ExplainerVideo";

/**
 * Config-driven premium sign-in page for lenders, introducers and campaign partners.
 * `kind = "lender" | "introducer" | "campaign"` — flips copy, hero pillars, and post-login redirect target.
 * A campaign partner, a call-centre manager and their agents all sign in to the introducer portal; it shapes
 * itself to the seat, so "campaign" only changes what the page says.
 */
export default function PortalSignIn({ kind }) {
  const isLender = kind === "lender";
  const isCampaign = kind === "campaign";
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("signin"); // "signin" | "recover"
  const [failed, setFailed] = useState(false);

  const cfg = isLender ? {
    eyebrow:  "For our lending partners",
    title:    "Deal flow curated to your criteria.",
    subtitle: "Sign in to see live applications routed to your desk. Update criteria, review documents, and progress deals with the Connexis credit team.",
    applyHref: "/lender/apply",
    applyLabel: "Apply as a lender",
    signinTestId: "lender-signin",
    home: "/lender",
    heading: "Sign in to your lender desk",
    pillars: [
      { Icon: ClipboardList, title: "Pre-qualified pipeline",    body: "Every deal routed to you matches your finance types, industry appetite, and deal-size range." },
      { Icon: TrendingUp,     title: "Move faster than the market", body: "Status transitions, notes, and files stay in one dashboard — no more scattered emails." },
      { Icon: Shield,         title: "Tight compliance",          body: "Decline reasons captured for every pass. Full audit trail on every application." },
    ],
  } : isCampaign ? {
    eyebrow:  "For call centres & media partners",
    title:    "One sign-in. The portal shapes itself to you.",
    subtitle: "Call-centre managers, their agents and media partners all sign in here. A manager sees the whole floor; an agent sees only the cases they put in; a media partner sees links, stats and earnings — never a client.",
    applyHref: "/partner-with-us#enquire",
    applyLabel: "Start a conversation",
    signinTestId: "campaign-signin",
    home: "/introducer",
    heading: "Sign in to your partner portal",
    pillars: [
      { Icon: Headphones, title: "Call-centre managers",   body: "Every case your floor submits, who put it in, its stage and what the desk is waiting on. Add or pause agents yourself." },
      { Icon: Users,      title: "Agents",                 body: "Your own password, your own cases. Put a case in, attach the documents, follow it to a decision — nothing else gets in the way." },
      { Icon: Megaphone,  title: "Media & advertising",    body: "Tracked links and QR codes, opens → applications → funded, and what each case is worth to you. Client data never leaves Connexis." },
    ],
  } : {
    eyebrow:  "For our introducers",
    title:    "Get paid on funded deals — properly.",
    subtitle: "Sign in to submit new applications, track them from new to funded, and download your monthly commission statement on Connexis letterhead.",
    applyHref: "/introducer/apply",
    applyLabel: "Apply as an introducer",
    signinTestId: "introducer-signin",
    home: "/introducer",
    heading: "Sign in to your introducer portal",
    pillars: [
      { Icon: Handshake, title: "Direct-to-lender routing",   body: "Your applications reach our lender panel the moment they're submitted — no gatekeepers." },
      { Icon: Wallet,    title: "Flexible commission models", body: "Revenue share, flat per funded deal, or cost-per-lead — whichever suits how you work." },
      { Icon: FileText,  title: "Statements on letterhead",   body: "Downloadable monthly PDFs with owed vs paid, VAT breakdown, and payout schedule." },
    ],
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (!res.ok) { toast.error(res.error || "Sign in failed"); setFailed(true); return; }
    // Enforce role affinity — if a lender-role signs into the introducer page, still redirect to their own home.
    const target = roleHome(res.user?.role) || cfg.home;
    nav(target, { replace: true });
  };

  return (
    <ExplainerProvider audience={isCampaign ? "introducer" : kind}>
      <PortalShell kind={kind} variant="signin" eyebrow={cfg.eyebrow} title={cfg.title} subtitle={cfg.subtitle}>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_440px] gap-10 lg:gap-14 items-start">
        {/* Value pillars — second on mobile, first on desktop */}
        <div className="space-y-3 order-2 lg:order-1">
          {cfg.pillars.map((p, i) => (
            <div key={i}
                 className="group rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4 flex items-start gap-3.5 transition-all duration-300 hover:border-[#0055FF]/40 hover:bg-white/[0.05] hover:-translate-y-0.5 animate-in fade-in slide-in-from-bottom-3 duration-700 fill-mode-both"
                 style={{ animationDelay: `${150 + i * 120}ms` }}
                 data-testid={`portal-pillar-${i}`}>
              <div className="grid place-items-center size-9 rounded-xl bg-[#0055FF]/15 border border-[#0055FF]/30 text-[#7da9ff] transition-transform duration-300 group-hover:scale-110 shrink-0">
                <p.Icon className="size-4" />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-white">{p.title}</div>
                <p className="mt-1 text-[13px] text-slate-400 leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}

          {/* Capability markers */}
          <div className="flex flex-wrap items-center gap-1.5 pt-3 animate-in fade-in duration-700 fill-mode-both" style={{ animationDelay: "550ms" }}>
            {[
              [FileSignature, "E-sign agreements"],
              [MessagesSquare, "Live deal rooms"],
              [Lock, "Bank-grade encryption"],
            ].map(([Icon, label]) => (
              <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.03] px-3 h-7 text-[11px] font-medium text-slate-300">
                <Icon className="size-3 text-[#7da9ff]" /> {label}
              </span>
            ))}
          </div>

          <div className="pt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-slate-400">
            <Zap className="size-3.5 text-[#7da9ff] shrink-0" />
            <span>Invite-only surface. Not a member yet?</span>
            <Link to={cfg.applyHref} className="text-white underline underline-offset-4 hover:text-[#7da9ff]" data-testid="portal-apply-inline">
              {cfg.applyLabel}
            </Link>
          </div>

          <div className="mt-6 pt-6 border-t border-white/[0.07] animate-in fade-in duration-700 fill-mode-both" style={{ animationDelay: "650ms" }}>
            <p className="text-[12.5px] text-slate-500">
              New to Connexis? Here's the four-minute version.
            </p>
            <WatchExplainerButton full className="mt-3.5" testId="portal-signin-watch-explainer" />
          </div>
        </div>

        {/* Sign-in card — first on mobile */}
        <div className="order-1 lg:order-2 relative rounded-3xl p-[1.5px] bg-gradient-to-b from-[#0055FF]/60 via-white/10 to-transparent shadow-[0_0_90px_-25px_rgba(0,85,255,0.55)] animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both" style={{ animationDelay: "100ms" }}>
          <div className="rounded-[calc(1.5rem-1.5px)] bg-[#0d1526]/95 backdrop-blur-xl p-6 sm:p-7" data-testid={cfg.signinTestId}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.24em] uppercase text-[#7da9ff]">
                <Building2 className="size-3.5" /> Secure sign-in
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/25 px-2.5 h-6 text-[10px] font-semibold text-emerald-300">
                <span className="relative flex size-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex rounded-full size-1.5 bg-emerald-400" />
                </span>
                LIVE
              </span>
            </div>
            <h2 className="mt-3 text-[22px] font-semibold text-white tracking-tight">
              {mode === "recover"
                ? "Get back into your account"
                : cfg.heading}
            </h2>
            <p className="mt-1 text-[12.5px] text-slate-400">
              {mode === "recover"
                ? "Forgotten your password, or never got round to setting one? Same button — we'll email you the right link."
                : "Use the email your invite was sent to."}
            </p>

            {mode === "recover" ? (
              <AccessLinkForm kind={isCampaign ? "introducer" : kind} defaultEmail={email} onBack={() => setMode("signin")} testid={cfg.signinTestId} />
            ) : (
            <form onSubmit={submit}>
            <div className="mt-5 space-y-3.5">
              <div>
                <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#0055FF]/40 focus-visible:border-[#0055FF]/50 transition-colors"
                       placeholder="you@company.com" data-testid={`${cfg.signinTestId}-email`} />
              </div>
              <div>
                <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Password</Label>
                <div className="relative">
                  <Input type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                         className="mt-1.5 h-11 pr-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#0055FF]/40 focus-visible:border-[#0055FF]/50 transition-colors"
                         placeholder="••••••••" data-testid={`${cfg.signinTestId}-password`} />
                  <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                          className="absolute right-3 top-1/2 -translate-y-1/2 mt-[3px] text-slate-500 hover:text-slate-300 transition-colors"
                          data-testid={`${cfg.signinTestId}-toggle-pw`} aria-label="Toggle password visibility">
                    {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </div>
            </div>

            <Button type="submit" disabled={busy}
                    className="mt-5 w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] rounded-xl font-semibold text-[14px] shadow-lg shadow-[#0055FF]/25 transition-all hover:shadow-[#0055FF]/40 active:scale-[0.99]"
                    data-testid={`${cfg.signinTestId}-submit`}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <>Sign in <ArrowRight className="size-4 ml-1" /></>}
            </Button>

            {failed && (
              <div className="mt-3.5 rounded-xl border border-amber-400/25 bg-amber-500/[0.07] p-3" data-testid={`${cfg.signinTestId}-failed-hint`}>
                <p className="text-[12px] text-amber-50/90 leading-relaxed">
                  That email and password didn't match. If you've never set a password — or can't remember it — we'll send you a fresh link.
                </p>
                <button type="button" onClick={() => setMode("recover")}
                        className="mt-1.5 text-[12px] font-semibold text-white underline underline-offset-2"
                        data-testid={`${cfg.signinTestId}-failed-recover`}>
                  Email me a link instead
                </button>
              </div>
            )}

            <button type="button" onClick={() => setMode("recover")}
                    className="mt-3.5 w-full text-center text-[12px] text-[#7da9ff] hover:text-white transition-colors"
                    data-testid={`${cfg.signinTestId}-forgot`}>
              Forgot your password — or never set one?
            </button>
            </form>
            )}

            <div className="mt-4 flex items-center justify-center gap-1.5 text-[10.5px] text-slate-500">
              <Lock className="size-3" /> 256-bit TLS · invite-only access · sessions expire automatically
            </div>

            <div className="mt-4 pt-4 border-t border-white/[0.06] text-center text-[12px] text-slate-400">
              No account yet?{" "}
              <Link to={cfg.applyHref} className="text-white underline underline-offset-4 hover:text-[#7da9ff]">
                {cfg.applyLabel}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
    </ExplainerProvider>
  );
}
